
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';

interface SeasonalDemandData {
  currentMonth: {
    rentals: number;
    revenue: number;
    name: string;
  };
  previousMonth: {
    rentals: number;
    revenue: number;
    name: string;
  };
  rentalsTrend: number;
  revenueTrend: number;
  seasonality: 'high' | 'medium' | 'low';
}

export const SeasonalDemandCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SeasonalDemandData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSeasonalData();
  }, []);

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('ru-RU', { month: 'long' });
  };

  const fetchSeasonalData = async () => {
    try {
      setLoading(true);
      
      const currentDate = new Date();
      const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const previousPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 2, 1);

      // Получаем данные по арендам и платежам за текущий и предыдущий месяцы
      const [currentRentals, previousRentals, currentPayments, previousPayments] = await Promise.all([
        supabase
          .from('rentals')
          .select('id')
          .gte('start_date', currentMonth.toISOString())
          .in('status', ['active', 'completed']),
        
        supabase
          .from('rentals')
          .select('id')
          .gte('start_date', previousMonth.toISOString())
          .lt('start_date', currentMonth.toISOString())
          .in('status', ['active', 'completed']),
        
        supabase
          .from('payments')
          .select('amount')
          .gte('payment_date', currentMonth.toISOString())
          .in('status', ['paid', 'completed']),
        
        supabase
          .from('payments')
          .select('amount')
          .gte('payment_date', previousMonth.toISOString())
          .lt('payment_date', currentMonth.toISOString())
          .in('status', ['paid', 'completed'])
      ]);

      if (currentRentals.error) throw currentRentals.error;
      if (previousRentals.error) throw previousRentals.error;
      if (currentPayments.error) throw currentPayments.error;
      if (previousPayments.error) throw previousPayments.error;

      const currentRentalsCount = currentRentals.data?.length || 0;
      const previousRentalsCount = previousRentals.data?.length || 0;
      const currentRevenue = currentPayments.data?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
      const previousRevenue = previousPayments.data?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

      const rentalsTrend = previousRentalsCount > 0 ? 
        ((currentRentalsCount - previousRentalsCount) / previousRentalsCount) * 100 : 0;
      const revenueTrend = previousRevenue > 0 ? 
        ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

      // Определяем сезонность на основе трендов
      let seasonality: 'high' | 'medium' | 'low' = 'medium';
      if (rentalsTrend > 20) seasonality = 'high';
      else if (rentalsTrend < -20) seasonality = 'low';

      setData({
        currentMonth: {
          rentals: currentRentalsCount,
          revenue: currentRevenue,
          name: getMonthName(currentDate)
        },
        previousMonth: {
          rentals: previousRentalsCount,
          revenue: previousRevenue,
          name: getMonthName(previousMonth)
        },
        rentalsTrend,
        revenueTrend,
        seasonality
      });

    } catch (err: any) {
      console.error('Ошибка при получении данных о сезонности:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-lg">
            <Calendar className="h-5 w-5 mr-2 text-purple-500" />
            Сезонность спроса
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-6 w-16" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-lg">
            <Calendar className="h-5 w-5 mr-2 text-purple-500" />
            Сезонность спроса
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-red-500">
            <p className="text-sm">Ошибка загрузки данных</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSeasonalityColor = (seasonality: string) => {
    switch (seasonality) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getSeasonalityText = (seasonality: string) => {
    switch (seasonality) {
      case 'high': return 'Высокий спрос';
      case 'low': return 'Низкий спрос';
      default: return 'Средний спрос';
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-lg">
          <Calendar className="h-5 w-5 mr-2 text-purple-500" />
          Сезонность спроса
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {data.currentMonth.rentals}
              </div>
              <div className="text-xs text-muted-foreground">
                аренд в {data.currentMonth.name}
              </div>
            </div>
            <div className="flex items-center space-x-1">
              {data.rentalsTrend >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
              <span className={`text-sm font-medium ${data.rentalsTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Math.abs(data.rentalsTrend).toFixed(1)}%
              </span>
            </div>
          </div>

          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeasonalityColor(data.seasonality)}`}>
            {getSeasonalityText(data.seasonality)}
          </div>

          <div className="text-xs text-muted-foreground">
            Доход: {data.currentMonth.revenue.toLocaleString('ru-RU', { 
              style: 'currency', 
              currency: 'RUB',
              minimumFractionDigits: 0
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
